import { useEffect, useState } from "react";
import {
  Box, Typography, Paper, Table, TableHead,
  TableRow, TableCell, TableBody, Button,
  TextField, Stack, Dialog, Chip, Grid,
  Alert, InputAdornment, ToggleButton,
  ToggleButtonGroup, FormControl, InputLabel,
  Select, MenuItem, Tooltip, IconButton, Divider,
  CircularProgress,
} from "@mui/material";
import SearchIcon           from "@mui/icons-material/Search";
import AddIcon              from "@mui/icons-material/Add";
import VisibilityIcon       from "@mui/icons-material/Visibility";
import SellIcon             from "@mui/icons-material/Sell";
import SwapHorizIcon        from "@mui/icons-material/SwapHoriz";
import EditIcon             from "@mui/icons-material/Edit";
import ElectricRickshawIcon from "@mui/icons-material/ElectricRickshaw";
import TwoWheelerIcon       from "@mui/icons-material/TwoWheeler";

import api                  from "../service/api";
import CreateStockForm      from "../component/CreateStockForm";
import SellBikeForm         from "../component/SellBikeForm";
import Updatestockform      from "../component/Updatestockform";
import TransferDialog       from "../component/TransferDialog";
import Passwordpromptdialog from "../component/Passwordpromptdialog";

/* ─── Helpers ────────────────────────────────────────────────────────────── */
const fmtMoney = (n) =>
  n !== undefined && n !== null && n !== ""
    ? "₹" + Number(n).toLocaleString("en-IN")
    : "—";

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
      })
    : "—";

const fmtBool = (v) =>
  v === true  ? <Chip label="Yes" size="small" color="success" variant="outlined" /> :
  v === false ? <Chip label="No"  size="small" color="default" variant="outlined" /> :
                "—";

/* ─── DetailRow ──────────────────────────────────────────────────────────── */
const DetailRow = ({ label, value, mono = false }) => {
  // Detect React element (e.g. Chip returned from fmtBool)
  const isJSX = value !== null && value !== undefined &&
                typeof value === "object" && !Array.isArray(value) &&
                ("$$typeof" in value || "type" in value);
  return (
    <Box sx={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      py: 0.8, borderBottom: "0.5px solid", borderColor: "divider",
      "&:last-child": { borderBottom: "none" },
    }}>
      <Typography
        variant="caption" color="text.secondary" fontWeight={600}
        sx={{ fontSize: 11, letterSpacing: 0.3, minWidth: 155, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Box textAlign="right" sx={{ flex: 1, ml: 1 }}>
        {isJSX ? value : (
          <Typography
            variant="body2" fontWeight={500}
            sx={{ fontFamily: mono ? "monospace" : "inherit", fontSize: 13, wordBreak: "break-all" }}
          >
            {value ?? "—"}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/* ─── SectionCard ────────────────────────────────────────────────────────── */
const SCard = ({ title, color = "#1565C0", bg = "#E3F2FD", children, fullWidth = false }) => (
  <Grid item xs={12} md={fullWidth ? 12 : 6}>
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "0.5px solid", borderColor: "divider", height: "100%" }}>
      <Box sx={{ display: "inline-flex", px: 1.2, py: 0.3, borderRadius: 1.5, bgcolor: bg, mb: 1.5 }}>
        <Typography variant="caption" fontWeight={700} sx={{ color, fontSize: 11, letterSpacing: 0.5 }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Paper>
  </Grid>
);

/* ─── StatusChip ─────────────────────────────────────────────────────────── */
const StatusChip = ({ status }) => {
  const map = {
    IN_STOCK: ["success", "In stock"],
    SOLD:     ["error",   "Sold"],
    RETURN:   ["warning", "Return"],
  };
  const [color, label] = map[status] || ["default", status || "—"];
  return <Chip label={label} color={color} size="small" />;
};

/* ─── VehicleChip ────────────────────────────────────────────────────────── */
const VehicleChip = ({ type }) =>
  type === "E_RICKSHAW" ? (
    <Chip
      icon={<ElectricRickshawIcon sx={{ fontSize: 14 }} />}
      label="E-Rickshaw" size="small"
      sx={{ bgcolor: "#FFF3E0", color: "#E65100", border: "1px solid #FFCC80" }}
    />
  ) : (
    <Chip
      icon={<TwoWheelerIcon sx={{ fontSize: 14 }} />}
      label="Scooty" size="small"
      sx={{ bgcolor: "#FCE4EC", color: "#880E4F", border: "1px solid #F48FB1" }}
    />
  );

/* ═══════════════════════════════════════════════════════════════════════════ */
const CreateStock = () => {

  const [data,         setData]         = useState([]);
  const [search,       setSearch]       = useState("");
  const [typeFilter,   setTypeFilter]   = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("");

  const [openCreate,         setOpenCreate]         = useState(false);
  const [openSell,           setOpenSell]           = useState(false);
  const [openView,           setOpenView]           = useState(false);
  const [openTransfer,       setOpenTransfer]       = useState(false);
  const [openPasswordPrompt, setOpenPasswordPrompt] = useState(false);
  const [openUpdate,         setOpenUpdate]         = useState(false);
const [openSellPassword, setOpenSellPassword] = useState(false);

  const [selectedBike, setSelectedBike] = useState(null);
  const [bikeToEdit,   setBikeToEdit]   = useState(null);
  const [viewData,     setViewData]     = useState(null);
  const [viewLoading,  setViewLoading]  = useState(false);
  const [viewError,    setViewError]    = useState("");

  /* ── Fetch all stock ─────────────────────────────────────────────────── */
  const fetchStock = async () => {
    try {
      const res = await api.get("/filter-bikes");
      setData(res.data?.data || res.data || []);
    } catch (err) {
      console.error("Failed to fetch EV stock:", err);
    }
  };

  useEffect(() => { fetchStock(); }, []);

  /* ── Filter ──────────────────────────────────────────────────────────── */
  const filtered = data.filter((b) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      (b.engine      || "").toLowerCase().includes(q) ||
      (b.chasis      || "").toLowerCase().includes(q) ||
      (b.model       || "").toLowerCase().includes(q) ||
      (b.companyName || "").toLowerCase().includes(q) ||
      (b.invoiceNo   || "").toLowerCase().includes(q);
    const matchType   = typeFilter === "ALL" || b.vehicleType === typeFilter;
    const matchStatus = !statusFilter || b.status === statusFilter;
    return matchSearch && matchType && matchStatus;
  });

  /* ── Summary counts ──────────────────────────────────────────────────── */
  const totalInStock = data.filter((b) => b.status === "IN_STOCK").length;
  const totalSoldBike    = data.filter((b) => b.status === "SOLD" && b.vehicleType === "SCOOTY").length;
  const totalSoldE_RICKSHAW   = data.filter((b) => b.status === "SOLD" && b.vehicleType === "E_RICKSHAW").length;

  const erCount      = data.filter((b) => b.status === "IN_STOCK" && b.vehicleType === "E_RICKSHAW" ).length;
  const scCount      = data.filter((b) => b.status === "IN_STOCK" && b.vehicleType === "SCOOTY" ).length;

  /* ── Handlers ────────────────────────────────────────────────────────── */
   const handleSellOpen        = (bike) => { setSelectedBike(bike); setOpenSellPassword(true); };
const handleSellPasswordSuccess = ()  => { setOpenSellPassword(false); setOpenSell(true); };
const handleSellPasswordClose   = ()  => { setOpenSellPassword(false); setSelectedBike(null); };

  const handleTransferOpen    = (bike) => { setSelectedBike(bike); setOpenTransfer(true); };
  const handleTransferClose   = ()     => { setOpenTransfer(false); setSelectedBike(null); };
  const handleEditClick       = (bike) => { setBikeToEdit(bike); setOpenPasswordPrompt(true); };
  const handlePasswordSuccess = ()     => { setOpenPasswordPrompt(false); setOpenUpdate(true); };
  const handlePasswordClose   = ()     => { setOpenPasswordPrompt(false); setBikeToEdit(null); };
  const handleUpdateClose     = ()     => { setOpenUpdate(false); setBikeToEdit(null); };

  /* ── View: fetch single bike ─────────────────────────────────────────── */
  const handleView = async (id) => {
    setViewLoading(true);
    setViewError("");
    setViewData(null);
    setOpenView(true);
    try {
      const res = await api.get(`/specific-bike-details/${id}`);
      // Controller: { success, message, data: responseData }
      setViewData(res.data?.data || res.data);
      console.log(res.data.data)
    } catch (err) {
      setViewError(err?.response?.data?.message || "Failed to load details.");
    } finally {
      setViewLoading(false);
    }
  };

  const handleViewClose = () => {
    setOpenView(false);
    setViewData(null);
    setViewError("");
  };

  /* ════════════════════════════════════════════════════════════════════════
   *  RENDER
   * ════════════════════════════════════════════════════════════════════════ */
  return (
    <Box sx={{ ml: { xs: 0, sm: 0, lg: "210px" }, p: { xs: 2, md: 3 }, minHeight: "100vh", bgcolor: "#F8FAFC" }}>

      {/* ── Header ────────────────────────────────────────────────────── */}
      <Box mb={3}>
        <Typography variant="h4" fontWeight={700}>EV Stock Management</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Electric vehicles inventory — E-Rickshaw &amp; Scooty
        </Typography>
      </Box>

      {/* ── Summary cards ─────────────────────────────────────────────── */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: "Total in stock",       value: totalInStock, color: "#1565C0", bg: "#E3F2FD" },
          { label: "E-Rickshaw available", value: erCount,      color: "#2E7D32", bg: "#E8F5E9"},
          { label: "Scooty available",     value: scCount,      color: "#2E7D32", bg: "#E8F5E9" },
          { label: "Total sold",           value: `${totalSoldBike + totalSoldE_RICKSHAW}`,         color: "#E65100", bg: "#FFF3E0" },
          { label: "Sold E-Rickshaw",      value: totalSoldE_RICKSHAW,          color: "#E65100", bg: "#FFF3E0" },
          { label: "Sold Scooty",          value: totalSoldBike,      color: "#E65100", bg: "#FFF3E0"         }
        ].map((c) => (
          <Grid item xs={6} md={3} key={c.label}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider", bgcolor: c.bg }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {c.label.toUpperCase()}
              </Typography>
              <Typography variant="h4" fontWeight={700} color={c.color} mt={0.5}>{c.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ── Toolbar ───────────────────────────────────────────────────── */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2} alignItems="center">
        <TextField
          placeholder="Search engine / chassis / model / company / invoice..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          size="small" sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          value={typeFilter} exclusive size="small"
          onChange={(_, v) => v && setTypeFilter(v)}
        >
          <ToggleButton value="ALL">All</ToggleButton>
          <ToggleButton value="E_RICKSHAW">E-Rickshaw</ToggleButton>
          <ToggleButton value="SCOOTY">Scooty</ToggleButton>
        </ToggleButtonGroup>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="IN_STOCK">In stock</MenuItem>
            <MenuItem value="SOLD">Sold</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained" startIcon={<AddIcon />}
          onClick={() => setOpenCreate(true)} sx={{ whiteSpace: "nowrap" }}
        >
          Add stock
        </Button>
      </Stack>

      {/* ── Table ─────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#F1F5F9" }}>
              {[
                "Sl no.", "Type", "Company / Model", "Colour",
                "Engine no.", "Chassis no.", "Location",
                 "Status", "Actions",
              ].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 5, color: "text.secondary" }}>
                  No vehicles found
                </TableCell>
              </TableRow>
            ) : filtered.map((bike) => (
              <TableRow key={bike._id} hover>
                <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>{bike.slNo}</TableCell>
                <TableCell><VehicleChip type={bike.vehicleType} /></TableCell>
                <TableCell>
                  <Typography fontSize={13} fontWeight={600}>{bike.model}</Typography>
                  <Typography fontSize={11} color="text.secondary">{bike.companyName}</Typography>
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>{bike.colour || "—"}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{bike.engine || "N/A"}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{bike.chasis || "N/A"}</TableCell>
                {/* list view uses stock.location directly (not mapped to currentLocation) */}
                <TableCell sx={{ fontSize: 13 }}>{bike.location || "—"}</TableCell>
               
                <TableCell><StatusChip status={bike.status} /></TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="View all details">
                      <IconButton size="small" onClick={() => handleView(bike._id)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit (password required)">
                      <IconButton size="small" onClick={() => handleEditClick(bike)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={bike.status === "SOLD" ? "Already sold" : "Sell vehicle"}>
                      <span>
                        <Button
                          size="small" variant="contained" color="success"
                          startIcon={<SellIcon sx={{ fontSize: 13 }} />}
                          disabled={bike.status === "SOLD"}
                          onClick={() => handleSellOpen(bike)}
                          sx={{ fontSize: 11, py: 0.3, px: 1 }}
                        >
                          Sell
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title="Transfer to another location">
                      <Button
                        size="small" variant="outlined"
                        startIcon={<SwapHorizIcon sx={{ fontSize: 13 }} />}
                        onClick={() => handleTransferOpen(bike)}
                        sx={{ fontSize: 11, py: 0.3, px: 1 }}
                      >
                        Transfer
                      </Button>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* ════════════════════════════════════════════════════════════════
       *  DIALOGS
       * ════════════════════════════════════════════════════════════════ */}

      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
        <CreateStockForm
          onSuccess={() => { fetchStock(); setOpenCreate(false); }}
          onClose={() => setOpenCreate(false)}
        />
      </Dialog>
      <Passwordpromptdialog
  open={openSellPassword}  onClose={handleSellPasswordClose}
  onSuccess={handleSellPasswordSuccess}  type="sellEv"  title="Verify to Sell Vehicle"
/>

      <Dialog open={openSell} onClose={() => setOpenSell(false)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
        <SellBikeForm bike={selectedBike} onClose={() => setOpenSell(false)} onSuccess={fetchStock} />
      </Dialog>

      <Passwordpromptdialog
        open={openPasswordPrompt} onClose={handlePasswordClose}
        onSuccess={handlePasswordSuccess} type="stockUpdate" title="Verify to Edit Stock"
      />

      <Dialog open={openUpdate} onClose={handleUpdateClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
        <Updatestockform bike={bikeToEdit} onClose={handleUpdateClose} onSuccess={fetchStock} />
      </Dialog>

      <TransferDialog
        open={openTransfer} onClose={handleTransferClose}
        bike={selectedBike} onSuccess={fetchStock}
      />

 {/* view data */}
      <Dialog
        open={openView} onClose={handleViewClose}
        fullWidth maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 3, maxHeight: "92vh" } }}
      >
        <Box p={3} sx={{ overflowY: "auto", bgcolor: "#F8FAFC" }}>

          {/* Header */}
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
            <Box>
              <Typography variant="h6" fontWeight={700}>Vehicle full details</Typography>
              {viewData && (
                <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                  <VehicleChip type={viewData.vehicleType} />
                  {viewData.model       && <Chip label={viewData.model}       size="small" color="primary" variant="outlined" />}
                  {viewData.companyName && <Chip label={viewData.companyName} size="small" variant="outlined" />}
                  <StatusChip status={viewData.status} />
                  {viewData.source && (
                    <Chip
                      label={viewData.source} size="small" variant="outlined"
                      color={viewData.source === "EXCHANGE" ? "warning" : "info"}
                    />
                  )}
                </Box>
              )}
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              {viewData?.slNo && (
                <Chip label={`Sl. ${viewData.slNo}`} size="small" sx={{ fontWeight: 700 }} />
              )}
              <IconButton size="small" onClick={handleViewClose}>✕</IconButton>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Loading */}
          {viewLoading && (
            <Box display="flex" alignItems="center" gap={2} py={6} justifyContent="center">
              <CircularProgress size={24} />
              <Typography color="text.secondary">Loading vehicle details…</Typography>
            </Box>
          )}

          {/* Error */}
          {viewError && <Alert severity="error" sx={{ mb: 2 }}>{viewError}</Alert>}

          {/* ── All sections ─────────────────────────────────────────── */}
          {!viewLoading && viewData && (
            <Grid container spacing={2}>

              {/* ① VEHICLE IDENTIFICATION
                  schema: slNo, vehicleType, source, date, invoiceNo,
                           companyName, model, colour, chasis, engine, HSN, quantity */}
              <SCard title="VEHICLE IDENTIFICATION" color="#1565C0" bg="#E3F2FD">
                <DetailRow label="Serial no."   value={viewData.slNo} />
                <DetailRow
                  label="Vehicle type"
                  value={
                    viewData.vehicleType === "E_RICKSHAW" ? "E-Rickshaw"
                    : viewData.vehicleType === "SCOOTY"   ? "Scooty"
                    : viewData.vehicleType
                  }
                />
                <DetailRow label="Source"       value={viewData.source} />
                <DetailRow label="Date"         value={fmtDate(viewData.date)} />
                <DetailRow label="Invoice no."  value={viewData.invoiceNo} />
                <DetailRow label="Company name" value={viewData.companyName} />
                <DetailRow label="Model"        value={viewData.model} />
                <DetailRow label="Colour"       value={viewData.colour} />
                <DetailRow label="Engine no."   value={viewData.engine} mono />
                <DetailRow label="Chassis no."  value={viewData.chasis} mono />
                <DetailRow label="HSN code"     value={viewData.HSN} />
 <DetailRow label="Sale price(Lithium)"      value={fmtMoney(viewData.salePrice)} />
 <DetailRow label="Sale price (lead)" value={fmtMoney(viewData.salePriceLead)} />

               </SCard>

              
              

              {/* ③ BATTERY & WARRANTY
                  schema: batteryType, oamBattery, warrantyPeriod */}
              <SCard title="Type of Battery Received" color="#6A1B9A" bg="#F3E5F5">
                {/* <DetailRow label="Battery type"     value={viewData.batteryType} /> */}
                                {/* <DetailRow label="Quantity"     value={viewData.quantity} /> */}

                <DetailRow label="OAM battery"      value={fmtBool(viewData.oamBattery)} />
                {/* <DetailRow label="Battery warranty" value={fmtDate(viewData.warrantyPeriod)} /> */}
              </SCard>

              {/* ④ CHARGER
                  schema: charger, chargerWarranty */}
              {/* <SCard title="CHARGER" color="#880E4F" bg="#FCE4EC">
                <DetailRow label="Charger serial"   value={viewData.charger} />
                <DetailRow label="Charger warranty" value={fmtDate(viewData.chargerWarranty)} />
              </SCard> */}

              {/* ⑤ CONTROLLER & MOTOR
                  schema: controllerNo, controllerWarranty, motorWarranty */}
              <SCard title="CONTROLLER & MOTOR" color="#00695C" bg="#E0F2F1">
                <DetailRow label="Controller no."      value={viewData.controllerNo} mono />
                <DetailRow label="Controller warranty" value={ (viewData.controllerWarranty)+" Months"} />
                <DetailRow label="Motor warranty"      value={ (viewData.motorWarranty)+" Months"} />
              </SCard>

              {/* ⑥ STATUS & LOCATION
                  schema: location (→ currentLocation), status,
                           invoiceStatus, form22Received, saleDate
                  controller: soldBy (populated) */}
              <SCard title="STATUS & LOCATION" color="#E65100" bg="#FFF3E0">
                <DetailRow label="Current location" value={viewData.currentLocation} />
                <DetailRow label="Status"           value={<StatusChip status={viewData.status} />} />
                <DetailRow label="Invoice issued"   value={fmtBool(viewData.invoiceStatus)} />
                <DetailRow label="Form 22 received" value={fmtBool(viewData.form22Received)} />
                <DetailRow label="Sale date"        value={fmtDate(viewData.saleDate)} />
                <DetailRow
                  label="Sold by"
                  value={viewData.soldBy?.name || (viewData.soldBy ? String(viewData.soldBy) : null)}
                />
              </SCard>

              {/* ⑦ AUDIT
                  schema: createdBy (populated), timestamps */}
              <SCard title="AUDIT" color="#546E7A" bg="#ECEFF1">
                <DetailRow label="Created by"  value={viewData.createdBy?.name  || viewData.createdBy} />
                <DetailRow label="Email"       value={viewData.createdBy?.email} />
                <DetailRow label="Role"        value={viewData.createdBy?.role} />
                <DetailRow label="Created at"  value={fmtDate(viewData.createdAt)} />
                <DetailRow label="Updated at"  value={fmtDate(viewData.updatedAt)} />
              </SCard>

              {/* ⑧ REMARKS — full width, only when present
                  schema: remarks */}
              {viewData.remarks && (
                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "0.5px solid", borderColor: "divider" }}>
                    <Box sx={{ display: "inline-flex", px: 1.2, py: 0.3, borderRadius: 1.5, bgcolor: "#ECEFF1", mb: 1 }}>
                      <Typography variant="caption" fontWeight={700} sx={{ color: "#546E7A", fontSize: 11 }}>
                        REMARKS
                      </Typography>
                    </Box>
                    <Typography fontSize={13} color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.8 }}>
                      {viewData.remarks}
                    </Typography>
                  </Paper>
                </Grid>
              )}

              {/* ⑨ SALE DETAILS — from saleModel (separate collection)
                  controller: saleDetails = saleModel.findOne({ bikeId })
                  Only rendered when saleDetails is not null */}
              {viewData.saleDetails && (
                <SCard title="SALE DETAILS" color="#1B5E20" bg="#E8F5E9" fullWidth>
                   
                    <Grid item xs={12} md={6}>
                     
                       <DetailRow
                        label="Sale date"
                        value={fmtDate(viewData.saleDetails.saleDate || viewData.saleDetails.date)}
                      />
                      <DetailRow
                        label="Sold by"
                        value={viewData.saleDetails.soldBy?.name || viewData.saleDetails.soldBy}
                      />
                   </Grid>
                </SCard>
              )}

              {/* ⑩ LOCATION COST TRACKING
                  schema: locationCost[{ location, totalCost }] */}
              {viewData.locationCost?.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "0.5px solid", borderColor: "divider", height: "100%" }}>
                    <Box sx={{ display: "inline-flex", px: 1.2, py: 0.3, borderRadius: 1.5, bgcolor: "#FFF3E0", mb: 1.5 }}>
                      <Typography variant="caption" fontWeight={700} sx={{ color: "#E65100", fontSize: 11 }}>
                        LOCATION COST TRACKING ({viewData.locationCost.length})
                      </Typography>
                    </Box>
                    {viewData.locationCost.map((lc, i) => (
                      <Box key={i} sx={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        py: 0.8, borderBottom: "0.5px solid", borderColor: "divider",
                        "&:last-child": { borderBottom: "none" },
                      }}>
                        <Typography fontSize={13} color="text.secondary">{lc.location}</Typography>
                        <Typography fontSize={13} fontWeight={700}>{fmtMoney(lc.totalCost)}</Typography>
                      </Box>
                    ))}
                  </Paper>
                </Grid>
              )}

              {/* ⑪ TRANSFER HISTORY
                  schema: transferHistory[{
                    fromLocation, toLocation, transferChallanNo,
                    transferCost, date, transferredBy (ref User)
                  }]
                  controller: transferredBy populated with name, email */}
              <Grid item xs={12} md={viewData.locationCost?.length > 0 ? 6 : 12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "0.5px solid", borderColor: "divider", height: "100%" }}>
                  <Box sx={{ display: "inline-flex", px: 1.2, py: 0.3, borderRadius: 1.5, bgcolor: "#E3F2FD", mb: 1.5 }}>
                    <Typography variant="caption" fontWeight={700} sx={{ color: "#1565C0", fontSize: 11 }}>
                      TRANSFER HISTORY ({viewData.transferHistory?.length || 0})
                    </Typography>
                  </Box>

                  {!viewData.transferHistory?.length ? (
                    <Typography fontSize={13} color="text.secondary">No transfers recorded</Typography>
                  ) : (
                    viewData.transferHistory.map((t, i) => (
                      <Box key={i} sx={{
                        mb: 1.5, p: 1.5, borderRadius: 1.5,
                        bgcolor: "#F8FAFC", border: "0.5px solid", borderColor: "divider",
                        "&:last-child": { mb: 0 },
                      }}>
                        {/* Route arrow */}
                        <Box display="flex" alignItems="center" gap={1} mb={0.8}>
                          <Chip
                            label={t.fromLocation || "—"} size="small"
                            sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontSize: 11 }}
                          />
                          <Typography fontSize={12} color="text.secondary">→</Typography>
                          <Chip
                            label={t.toLocation || "—"} size="small"
                            sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontSize: 11 }}
                          />
                        </Box>
                        {/* Transfer metadata */}
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.5 }}>
                          <Typography fontSize={12} color="text.secondary">
                            Date: <strong>{fmtDate(t.date)}</strong>
                          </Typography>
                          <Typography fontSize={12} color="text.secondary">
                            By: <strong>{t.transferredBy?.name || t.transferredBy || "—"}</strong>
                          </Typography>
                          {t.transferChallanNo && (
                            <Typography fontSize={12} color="text.secondary">
                              Challan: <strong>{t.transferChallanNo}</strong>
                            </Typography>
                          )}
                          {t.transferCost > 0 && (
                            <Typography fontSize={12} color="text.secondary">
                              Cost: <strong>{fmtMoney(t.transferCost)}</strong>
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))
                  )}
                </Paper>
              </Grid>

            </Grid>
          )}

          {/* Footer close */}
          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button variant="outlined" onClick={handleViewClose}>Close</Button>
          </Box>
        </Box>
      </Dialog>

    </Box>
  );
};

export default CreateStock;